import React, { useState, useEffect } from 'react';
import { Switch, Input, Checkbox } from 'antd';
import axios from 'axios';
import configData from '../config/config.json';

const SENSITIVE_BLOCK_IDS = [
    "IAB25-1", "IAB25-2", "IAB25-3", "IAB25-4", "IAB25-5", "IAB25-6", "IAB25-7",
    "IAB26-1", "IAB26-2", "IAB26-3", "IAB26-4",
    "IAB9-9", "IAB8-18", "IAB8-5", "IAB11-4",
];

function CategoryRows({
    node,
    selected,
    setSelected,
    searchTerm = "",
    disabled = false,
}) {
    const [open, setOpen] = useState(false);

    const hasChildren = node.children && node.children.length > 0;
    const childIds = node.children?.map(c => c.id) || [];

    const allChildrenSelected = hasChildren && childIds.every(id => selected.has(id));
    const someChildrenSelected = hasChildren && childIds.some(id => selected.has(id)) && !allChildrenSelected;
    const parentChecked = hasChildren ? allChildrenSelected : selected.has(node.id);
    const parentActive = hasChildren ? (allChildrenSelected || someChildrenSelected) : selected.has(node.id);

    // filter visibility (must be before useEffects that use childMatches)
    const parentMatches = node.name.toLowerCase().includes(searchTerm);
    const childMatches = (node.children || []).filter(c =>
        c.name.toLowerCase().includes(searchTerm)
    );

    // Close dropdown when parent is toggled off
    useEffect(() => {
        if (!hasChildren) return;
        if (!parentActive) setOpen(false);
    }, [parentActive, hasChildren]);

    // Auto open when search matches children
    useEffect(() => {
        if (!hasChildren) return;
        if (searchTerm) setOpen(childMatches.length > 0);
    }, [searchTerm, hasChildren, childMatches.length]);

    const toggleParent = () => {
        setSelected(prev => {
            const next = new Set(prev);
            if (hasChildren) {
                if (someChildrenSelected || allChildrenSelected) {
                    childIds.forEach(id => next.delete(id));
                } else {
                    childIds.forEach(id => next.add(id));
                }
            } else {
                if (next.has(node.id)) next.delete(node.id);
                else next.add(node.id);
            }
            return next;
        });
    };

    const toggleChild = (childId) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(childId)) next.delete(childId);
            else next.add(childId);
            return next;
        });
    };

    if (searchTerm && !parentMatches && childMatches.length === 0) return null;

    return (
        <>
            {/* Parent row */}
            <tr className={`${someChildrenSelected ? "is-partial" : ""}`}>
                <td className="cell checkbox-col">
                    <Switch
                        className={someChildrenSelected ? "switch-partial" : ""}
                        disabled={disabled}
                        checked={parentChecked || someChildrenSelected}
                        onChange={toggleParent}
                    />
                </td>
                <td className="cell name-col td_with_select_checkbox">
                    {hasChildren ? (
                        <button type="button" onClick={() => setOpen(o => !o)}>
                            {open
                                ? <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 1.5L6 6.5L11 1.5" stroke="#98A2B3" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                : <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.5 11L6.5 6L1.5 1" stroke="#98A2B3" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            }
                        </button>
                    ) : <></>}
                    <span className={`label ${!hasChildren ? "no-children m-0" : ""}`}>
                        {node.name}
                    </span>
                </td>
            </tr>
            {/* Children rows */}
            {open &&
                (node.children || [])
                    .filter(ch => !searchTerm || ch.name.toLowerCase().includes(searchTerm))
                    .map(ch => (
                        <tr key={ch.id} className={selected.has(ch.id) ? "is-selected" : ""}>
                            <td className="cell checkbox-col" />
                            <td className="cell name-col child">
                                <Switch
                                    disabled={disabled}
                                    checked={selected.has(ch.id)}
                                    onChange={() => toggleChild(ch.id)}
                                />
                                <span className="label no-children m-0">{ch.name}</span>
                            </td>
                        </tr>
                    ))
            }
        </>
    );
}

const BlockCategoryTable = ({
    selected,
    setSelected,
    disabled = false,
    normalizeKey = undefined,
}) => {
    const [DATA, setDATA] = useState([]);
    const [search, setSearch] = useState("");
    const searchTerm = search.trim().toLowerCase();

    const blockAllChecked = SENSITIVE_BLOCK_IDS.every(id => selected.has(id));
    const handleBlockAll = () => {
        setSelected(prev => {
            const next = new Set(prev);
            if (blockAllChecked) {
                SENSITIVE_BLOCK_IDS.forEach(id => next.delete(id));
            } else {
                SENSITIVE_BLOCK_IDS.forEach(id => next.add(id));
            }
            return next;
        });
    };

    const visibleNodes = DATA.filter(node => {
        const parentMatches = node.name.toLowerCase().includes(searchTerm);
        const childMatches = (node.children || []).some(c =>
            c.name.toLowerCase().includes(searchTerm)
        );
        return !searchTerm || parentMatches || childMatches;
    });

    useEffect(() => {
        axios
            .get(configData.SERVER_URL + "user/iabCat", {
                headers: { "Content-Type": "application/json" },
            })
            .then((response) => {
                setDATA(response.data.data);
            })
            .catch(() => { });
    }, []);

    // Normalize blockCategory: expand any parent IDs (e.g. "IAB9") to their children.
    // Runs when DATA loads OR when normalizeKey changes (publisher change in parent).
    useEffect(() => {
        if (!DATA || DATA.length === 0) return;
        setSelected(prev => {
            const next = new Set();
            prev.forEach(id => {
                const parent = DATA.find(cat => cat.id === id);
                if (parent && parent.children && parent.children.length > 0) {
                    parent.children.forEach(ch => next.add(ch.id));
                } else {
                    next.add(id);
                }
            });
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [DATA, normalizeKey]);

    return (
        <div className="block-category block_category_adjustment --table_width_css">
            <div className="form_content_block--group mb-0">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d_flex justify_content_between mb_10 align-items-center">
                        <Input
                            type="text"
                            className="form_control basic md search_input w_table_search"
                            placeholder="Search"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <div className='primary_checkbox'>
                        <Checkbox
                            checked={blockAllChecked}
                            onChange={handleBlockAll}
                            disabled={disabled}
                        >
                            <span className="form_label">Block all sensitive categories</span>
                        </Checkbox>
                    </div>
                </div>
            </div>
            <div className="table-body-scroll without_hover_tr">
                <table className="category-table --category_table_newspacing">
                    <tbody className="scroll-body">
                        {DATA.length > 0 ? (
                            visibleNodes.length === 0 ? (
                                <tr>
                                    <td colSpan={2} style={{ textAlign: "center", height: "20rem", width: "72rem" }}>
                                        <div className="no-data">No data found</div>
                                    </td>
                                </tr>
                            ) : (
                                visibleNodes.map(node => (
                                    <CategoryRows
                                        key={node.id}
                                        node={node}
                                        selected={selected}
                                        setSelected={setSelected}
                                        searchTerm={searchTerm}
                                        disabled={disabled}
                                    />
                                ))
                            )
                        ) : (
                            <tr>
                                <td colSpan={2} style={{ textAlign: "center", height: "20rem", width: "72rem" }}>
                                    <div className="no-data">No data found</div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BlockCategoryTable;
